import { useMemo, useState } from "react";
import {
  Layout,
  Typography,
  Button,
  Space,
  Card,
  Statistic,
  Avatar,
  Tooltip,
  Input,
  Flex,
  theme,
  Modal,
} from "antd";
import {
  FileTextOutlined,
  WarningOutlined,
  HistoryOutlined,
  FileAddOutlined,
  FilterOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import NavigationBar from "../../shared/components/NavigationBar";
import HeaderApp from "../../shared/components/HeaderApp";
const { Content } = Layout;
const { Text } = Typography;
import ContractCard from "../components/ContractCard";
import EmptyData from "../../shared/components/EmptyData";
import type { Contract } from "../types";
import dayjs from "dayjs";
import { useFetchContracts } from "../hooks/useFetchContracts";
import { useFetchContractsStats } from "../hooks/useFetchContractsStats";
import Loading from "../../shared/components/Loading";
import DrawerDetails from "../../shared/components/DrawerDetails";
import ContractDetails from "../components/ContractDetails";
import HeaderPage from "../../shared/components/HeaderPage";
import { useNavigate } from "react-router-dom";
import { useFetchCompany } from "../../company/hooks/useFetchCompany";
import { useFetchClients } from "../../clients/hooks/useFetchClients";
import { useFetchQuotes } from "../../quotes/hooks/useFetchQuotes";
import type { ClientData } from "../../clients/types";
import type { QuoteData } from "../../quotes/types";

const ContractsPage = () => {
  const { token } = theme.useToken();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  // Se guarda solo el id (no el objeto) para que, tras guardar una edición y
  // refrescar la lista, el panel abierto muestre los datos actualizados en
  // vez de quedarse con la foto del contrato tomada al momento del click.
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);

  const { data: fetchedContracts, isLoading } = useFetchContracts();
  const { data: companyData, isLoading: isLoadingCompany } = useFetchCompany();
  // La vista de contratos no trae los datos del cliente ligado, así que se
  // resuelven aquí vía id_quote -> Quote.id_client -> Client, igual que ya
  // se hace con `party` (empresa) para no depender de que el backend los una.
  const { data: clientsData } = useFetchClients();
  const { data: quotesData } = useFetchQuotes();

  const clientsById = useMemo(() => {
    const map = new Map<number, ClientData>();
    clientsData?.forEach((client) => {
      if (client.id_client != null) map.set(client.id_client, client);
    });
    return map;
  }, [clientsData]);

  const quotesById = useMemo(() => {
    const map = new Map<number, QuoteData>();
    quotesData?.forEach((quote) => map.set(quote.id_quote, quote));
    return map;
  }, [quotesData]);

  const navigate = useNavigate();
  const mappedContracts: Contract[] = fetchedContracts?.map((c) => {
    let status = "vigente";
    let expiresLabel = "Sin vencimiento";
    let icon = <FileTextOutlined />;

    if (c.end_date) {
      const end = dayjs(c.end_date);
      const isPast = dayjs().isAfter(end, 'day');
      if (isPast) {
        status = "finalizado";
        expiresLabel = `Finalizó: ${end.format("DD MMM YYYY")}`;
        icon = <FolderOutlined />;
      } else {
        const daysLeft = end.diff(dayjs(), "day");
        if (daysLeft < 30) {
          status = "por_vencer";
          icon = <SafetyCertificateOutlined />;
        }
        expiresLabel = `Vence: ${end.format("DD MMM YYYY")}`;
      }
    }

    const quote = quotesById.get(c.id_quote);
    const client = quote ? clientsById.get(quote.id_client) : undefined;
    const rawClient = c as unknown as Partial<ClientData>;

    return {
      ...c,
      id: c.id_contract,
      title: c.contract_name,
      party: companyData,
      clientLabel: "Cliente",
      status: status as any,
      expiresLabel,
      icon,
      client_name: client?.client_name ?? rawClient.client_name,
      client_lastname_1: client?.client_lastname_1 ?? rawClient.client_lastname_1,
      client_lastname_2: client?.client_lastname_2 ?? rawClient.client_lastname_2,
      client_email: client?.client_email ?? rawClient.client_email,
      id_client: client?.id_client ?? rawClient.id_client,
    };
  }) || [];

  const selectedContract = selectedContractId != null
    ? mappedContracts.find((c) => c.id === selectedContractId) ?? null
    : null;

  const { data: statsData } = useFetchContractsStats();

  const vigentesCount = statsData?.vigentes || 0;
  const porVencerCount = statsData?.por_vencer || 0;
  const finalizadosCount = statsData?.finalizados || 0;

  const filteredContracts = mappedContracts.filter((c) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return (
      c.title.toLowerCase().includes(lowerSearch) ||
      c.party.name_company?.toLowerCase().includes(lowerSearch) ||
      c.expiresLabel.toLowerCase().includes(lowerSearch)
    );
  });

  return (
    <>
      <HeaderApp sectionName={"Contratos"} showBack />
      {/* ── Content ── */}
      <Content
        style={{
          marginBottom: 64,
          padding: "24px 16px",
          marginLeft: "auto",
          marginRight: "auto",
          width: "100%",
          backgroundColor: token.colorBgLayout,
        }}
        className="max-width-container"
      >
        {/* Hero */}
        <HeaderPage
          title="Contratos"
          subtitle="Administra y monitorea la vigencia de tus acuerdos comerciales."
          actions={[
            {
              label: "Nuevo Contrato",
              icon: <FileAddOutlined />,
              type: 'primary',
              onClick: () => {
                navigate("/contrato/nuevo");
              },
            },
          ]}
        />

        {/* Stats */}
        <Flex gap={16} wrap style={{ marginBottom: 28 }}>
          <Card
            style={{
              flex: 1,
              minWidth: 180,
              borderRadius: token.borderRadiusLG,
            }}
            styles={{ body: { padding: "16px 20px" } }}
          >
            <Flex align="center" gap={16}>
              <Avatar
                size={45}
                icon={<CheckCircleOutlined />}
                style={{
                  background: token.colorSuccessBg,
                  color: token.colorSuccess,
                  borderRadius: token.borderRadiusLG,
                }}
              />
              <Statistic
                title={
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: token.colorTextSecondary,
                    }}
                  >
                    Vigentes
                  </Text>
                }
                value={vigentesCount}
                valueStyle={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}
              />
            </Flex>
          </Card>

          <Card
            style={{
              flex: 1,
              minWidth: 180,
              borderRadius: token.borderRadiusLG,
            }}
            styles={{ body: { padding: "16px 20px" } }}
          >
            <Flex align="center" gap={16}>
              <Avatar
                size={45}
                icon={<WarningOutlined />}
                style={{
                  background: token.colorErrorBg,
                  color: token.colorError,
                  borderRadius: token.borderRadiusLG,
                }}
              />
              <Statistic
                title={
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: token.colorTextSecondary,
                    }}
                  >
                    Por Vencer
                  </Text>
                }
                value={porVencerCount}
                valueStyle={{
                  fontSize: 18,
                  fontWeight: 900,
                  lineHeight: 1,
                  color: token.colorError,
                }}
              />
            </Flex>
          </Card>

          <Card
            style={{
              flex: 1,
              minWidth: 180,
              borderRadius: token.borderRadiusLG,
            }}
            styles={{ body: { padding: "16px 20px" } }}
          >
            <Flex align="center" gap={16}>
              <Avatar
                size={48}
                icon={<HistoryOutlined />}
                style={{
                  background: token.colorFillSecondary,
                  color: token.colorTextSecondary,
                  borderRadius: token.borderRadiusLG,
                }}
              />
              <Statistic
                title={
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: token.colorTextSecondary,
                    }}
                  >
                    Finalizados
                  </Text>
                }
                value={finalizadosCount}
                valueStyle={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}
              />
            </Flex>
          </Card>
        </Flex>

        {/* List Header */}
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 12, padding: "0 4px" }}
        >
          <Text strong style={{ fontSize: 15 }}>
            Listado Reciente
          </Text>
          <Space>
            {searchOpen && (
              <Input
                prefix={<SearchOutlined />}
                placeholder="Buscar contrato..."
                size="small"
                autoFocus
                style={{ borderRadius: 8, width: 200 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onBlur={() => {
                  if (!searchText) setSearchOpen(false);
                }}
              />
            )}
            <Tooltip title="Filtrar">
              <Button
                type="text"
                icon={<FilterOutlined />}
                shape="default"
                style={{ borderRadius: 8 }}
              />
            </Tooltip>
            <Tooltip title="Buscar">
              <Button
                type="text"
                icon={<SearchOutlined />}
                shape="default"
                style={{ borderRadius: 8 }}
                onClick={() => setSearchOpen(true)}
              />
            </Tooltip>
          </Space>
        </Flex>

        {/* Contract Cards */}
        <Flex vertical gap={12}>
          {isLoading || isLoadingCompany ? (
            <Loading />
          ) : filteredContracts.length === 0 ? (
            <EmptyData description="No se encontraron contratos" />
          ) : (
            filteredContracts.map((c) => (
              <ContractCard key={c.id} contract={c} onClick={() => setSelectedContractId(c.id)} />
            ))
          )}
        </Flex>
        <NavigationBar activeKey="" />
        {/* Drawer Details Overlay */}
        <DrawerDetails
          title="Detalle del Contrato"
          open={!!selectedContract}
          onClose={() =>
            Modal.confirm({
              title: "¿Estas seguro que deseas salir?",
              content: "Al cerrar se perderán los cambios no guardados.",
              okText: "Sí, cerrar",
              cancelText: "Cancelar",
              onOk: () => setSelectedContractId(null),
            })
          }
          size={'90%'}
          contentBody={selectedContract ? <ContractDetails contract={selectedContract} /> : null}
        />
      </Content>
    </>
  );
};

export default ContractsPage;
